import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { ButtonText } from '../Button';
import { Icon, IconTypes } from '../Icon';
import { H3 } from '../Typography';

interface ICardRowProps {
  marginBottom?: 'large' | 'small';
}

export const CardRow = styled.section<ICardRowProps>`
  position: relative;
  z-index: ${theme.zIndex.contentHeader};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${toRem(250)}, 1fr));
  grid-gap: ${toRem(11)};
  width: 100%;
  margin-bottom: ${toRem(theme.layout.gutter.small)};
  padding: 0 ${toRem(theme.layout.gutter.small)};

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: ${toRem(44)};
    padding: 0 ${toRem(theme.layout.gutter.large)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(22)};
    margin-top: ${toRem(-theme.card.minHeight.large / 2)};
  }

  ${({ marginBottom }) =>
    marginBottom === 'small' &&
    css`
      margin-bottom: ${toRem(theme.layout.gutter.small)};

      @media (min-width: ${theme.breakpoint.small}px) {
        margin-bottom: ${toRem(theme.layout.gutter.small)};
      }
    `}
`;

export const Card = styled.div<{ error?: boolean }>`
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: ${toRem(theme.card.minHeight.small)};
  padding: ${toRem(22)};
  border-radius: ${toRem(11)};
  ${(p) => css`
    border: ${toRem(1)} solid ${p.error ? theme.color.danger.light : theme.color.neutral[200]};
  `}

  background-color: ${theme.color.white};
  overflow: hidden;

  @media (min-width: ${theme.breakpoint.med}px) {
    min-height: ${toRem(theme.card.minHeight.large)};
  }

  &:focus {
    border-color: ${theme.color.neutral[400]};
    outline: none;
  }
`;

interface ICardIconProps {
  bgColor?: string;
  className?: string;
  icon: IconTypes;
  fill?: string;
  isLoading?: boolean;
}

const COLOR_DEFAULT = theme.color.neutral[900];

const CardIconLayout = styled.div<Partial<ICardIconProps>>`
  position: relative;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  width: ${toRem(33)};
  height: ${toRem(33)};
  margin-right: ${toRem(22)};
  border-radius: 50%;
  background-color: transparent;
  pointer-events: none;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${({ bgColor }) => bgColor || COLOR_DEFAULT};
    opacity: 0.15;
  }

  svg {
    width: ${toRem(16.5)};
    height: ${toRem(16.5)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    width: ${toRem(44)};
    height: ${toRem(44)};

    svg {
      width: ${toRem(22)};
      height: ${toRem(22)};
    }
  }

  ${({ bgColor }) =>
    bgColor &&
    css`
      background-color: ${bgColor};
    `}
`;

const CardIconLoading = styled(Skeleton)`
  && {
    width: ${toRem(33)};
    height: ${toRem(33)};
    margin-right: ${toRem(22)};
    border-radius: 50%;

    @media (min-width: ${theme.breakpoint.med}px) {
      width: ${toRem(44)};
      height: ${toRem(44)};
    }
  }
`;

export const CardIcon: React.FC<ICardIconProps> = ({ bgColor, className, icon, fill = theme.color.neutral[800], isLoading }) =>
  isLoading ? (
    <CardIconLoading />
  ) : (
    <CardIconLayout className={className} bgColor={bgColor} aria-hidden="true">
      <Icon icon={icon} fill={fill} />
    </CardIconLayout>
  );

interface ICardIconStatusProps {
  color: Appearance;
  icon: IconTypes;
  inverse?: boolean;
  isLoading?: boolean;
}

export const CardIconStatus: React.FC<ICardIconStatusProps> = ({ color, icon, isLoading, inverse }) => {
  let bgColor = inverse ? theme.color.neutral[400] : theme.color.neutral[100];
  let fill = inverse ? theme.color.white : theme.color.neutral[400];

  if (color === Appearance.PRIMARY) {
    bgColor = inverse ? theme.color.primary[500] : theme.color.primary[25];
    fill = inverse ? theme.color.white : theme.color.primary[500];
  }

  if (color === Appearance.NEUTRAL) {
    bgColor = inverse ? theme.color.neutral[400] : theme.color.neutral[100];
    fill = inverse ? theme.color.white : theme.color.neutral[500];
  }

  if (color === Appearance.SUCCESS) {
    bgColor = inverse ? theme.color.success.medium : theme.color.success.light;
    fill = inverse ? theme.color.white : theme.color.success.medium;
  }

  if (color === Appearance.WARNING) {
    bgColor = inverse ? theme.color.warning.medium : theme.color.warning.light;
    fill = inverse ? theme.color.white : theme.color.warning.medium;
  }

  if (color === Appearance.DANGER) {
    bgColor = inverse ? theme.color.danger.medium : theme.color.danger.light;
    fill = inverse ? theme.color.white : theme.color.danger.medium;
  }

  return <CardIcon bgColor={bgColor} icon={icon} fill={fill} isLoading={isLoading} />;
};

export const CardContent = styled.div`
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  width: auto;
  height: auto;
`;

const CardLabelLayout = styled.label`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: ${theme.color.neutral[500]};
`;

interface ICardElementProps {
  className?: string;
  children?: ReactNode;
  isLoading?: boolean;
}

export const CardLabel = ({ className, children, isLoading }: ICardElementProps) => (
  <CardLabelLayout className={className} data-cy="card-label">
    {isLoading ? <Skeleton width={100} /> : children}
  </CardLabelLayout>
);

export const CardValueLayout = styled(H3)`
  display: flex;
  flex-flow: row nowrap;
  margin-top: ${toRem(8.75)};
  line-height: 120%;
  color: ${theme.color.neutral[900]};
  word-break: break-word;

  em {
    font-weight: ${theme.font.weight.regular};
    font-style: normal;
  }
`;

export const CardValue = ({ className, children, isLoading }: ICardElementProps) => (
  <CardValueLayout className={className} data-cy="card-value">
    {isLoading ? <Skeleton width={75} /> : children}
  </CardValueLayout>
);

interface ICardExpirationProps {
  date: string;
  isLoading: boolean;
}

const CardExpirationLayout = styled.span`
  font-size: ${toRem(13)};
  margin-top: ${toRem(5)};
`;

const CardExpirationDate = styled.span`
  color: ${theme.color.neutral[700]};
  margin-right: ${toRem(10)};
`;

const CardExpirationStatus = styled.span`
  color: ${theme.color.danger.medium};
`;

export const getExpirationStatus = (expDate: string) => {
  const today = dayjs(new Date());
  const d = dayjs(dayjs(expDate, 'MM/YY')).diff(today, 'month');

  if (d < 1) {
    return d < 0 ? 'Expired' : 'Expires soon';
  }

  return '';
};

export const CardExpiration = ({ date, isLoading }: ICardExpirationProps) => (
  <CardExpirationLayout data-cy="card-expiration">
    {isLoading ? (
      <Skeleton width={75} />
    ) : (
      <>
        <CardExpirationDate>{date}</CardExpirationDate>
        <CardExpirationStatus>{getExpirationStatus(date)}</CardExpirationStatus>
      </>
    )}
  </CardExpirationLayout>
);

interface CardDetailProps {
  fontStyle?: 'normal' | 'italic';
}

const CardDetailLayout = styled.span<CardDetailProps>`
  margin-top: ${toRem(8.75)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(11)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  ${({ fontStyle }) =>
    fontStyle === 'italic' &&
    css`
      font-style: italic;
    `}
`;

export const CardDetail = ({ className, children, isLoading }: ICardElementProps) => (
  <CardDetailLayout className={className} data-cy="card-detail">
    {isLoading ? <Skeleton width={65} /> : children}
  </CardDetailLayout>
);

export const CardAction = styled(ButtonText)`
  margin-top: ${toRem(11)};
  font-size: ${toRem(11)};
  text-decoration: none;
  color: ${theme.color.primary[500]};

  &:hover {
    color: ${theme.color.primary[600]};
  }

  &:active,
  &:visited {
    color: ${theme.color.primary[500]};
  }
`;
